// web-v2/src/services/v3.service.js
//
// THE V3 ACCOUNT AND SEARCH READS (docs/v3-account-api.md), the policy number
// write (stage D2), the conversation reads and the drop zone's two doors
// (stage D3). Every call is against the real endpoint. The endpoint is behind V3_API_ENABLED, which
// defaults OFF, so until Track A's reader is mounted every call answers
// 404 v3_api_disabled. In a DEV build only, that answer (or a network error,
// the API not running at all) falls back to the contract fixture, and the
// result SAYS SO: `source: 'fixture'` is what the page turns into the badge.
// Never a silent fallback: a production build gets the error, and the page
// renders the error.
//
// The fixture is loaded here and nowhere else (guard:
// tests/v3-account-page.test.js), by a dynamic import inside the DEV-only
// fallback, so no surface can read it by accident and a production bundle
// does not carry its body. It is the reader's own output over the synthetic
// client (scripts/v3-step2/write-account-fixture.js), and the reader answers
// airgus null because no AIRgus runs on this branch yet; the DEV fallback adds
// the hand-written items of v3-account-airgus.fixture.json (same synthetic
// ids) so the next-thing line and item placement render against something.
// A production build never sees either file.
import { api, ApiError } from '@/lib/api.js';

const DEV = !!import.meta.env.DEV;

export const FIXTURE_BADGE = 'fixture data';

async function loadFixture() {
  const m = await import('@/fixtures/v3-account.fixture.json');
  const a = await import('@/fixtures/v3-account-airgus.fixture.json');
  return { ...m.default, airgus: a.default };
}

function fallsBack(e) {
  if (!DEV) return false;
  if (e instanceof ApiError) return e.status === 404 && e.code === 'v3_api_disabled';
  // fetch itself threw: connection refused, DNS, offline. api.js rethrows the
  // raw error for those; an ApiError with status 0 is its timeout.
  return !(e instanceof ApiError) || e.status === 0;
}

// Resolves { data, source: 'api' | 'fixture' }. A 303 for a merged account is
// followed by fetch itself (same-origin GET), so `data.account.id` may differ
// from `id`; the page re-routes on that.
export async function getV3Account(id) {
  try {
    const data = await api.get(`/api/v3/account/${encodeURIComponent(id)}`);
    return { data, source: 'api' };
  } catch (e) {
    if (!fallsBack(e)) throw e;
    return { data: await loadFixture(), source: 'fixture' };
  }
}

// THE ONE WRITE on the v3 mount (Track D stage D2): the policy number through
// PATCH /api/v3/policies/:id/policy-number, the policies writer behind it as
// source 'broker' with the signed-in broker as actor. Resolves the server's
// { ok, policy }: the row RE-READ after the write, never the request echoed;
// the caller then re-fetches the account so the AIRgus item clears from the
// reader. No fixture fallback: a write cannot be faked, so with the API off
// the ApiError (404 v3_api_disabled) reaches the input, which renders the
// code inline.
// The "not a renewal of this term" answer for a later term that only looks like the renewal (recorded with who and when; nothing linked).
export function setV3NotARenewal(termPolicyId, laterPolicyId) {
  return api.post(`/api/v3/policies/${encodeURIComponent(termPolicyId)}/not-a-renewal`, { later_policy_id: laterPolicyId });
}

// The Link control: this policy renews prior, the term the page shows as "looks renewed, not linked" beside it.
export function setV3PriorLink(successorPolicyId, priorPolicyId) {
  return api.post(`/api/v3/policies/${encodeURIComponent(successorPolicyId)}/prior`, { prior_policy_id: priorPolicyId });
}

// The rewrite link: the later term is the rewrite ('rewrite') or re-up ('reup') of this one (the one writer: deal-rewrite.service linkRewrite).
export function setV3RewriteLink(laterPolicyId, sourcePolicyId, reason = 'rewrite') {
  return api.post(`/api/v3/policies/${encodeURIComponent(laterPolicyId)}/rewrite-of`, { source_policy_id: sourcePolicyId, reason });
}

export function setV3PolicyNumber(policyId, policyNumber, reason) {
  return api.patch(`/api/v3/policies/${encodeURIComponent(policyId)}/policy-number`, { policy_number: policyNumber, ...(reason ? { reason } : {}) });
}

// THE CONVERSATION (Track D stage D3, Track A's docs/v3-mail-api.md, 5 and 6).
// One read per account, every term present with its messages newest first
// and the unattributed list beside them; the drill-in reads one message as
// sent. Both fall back to the contract fixtures under the same DEV gate as
// the account read (fallsBack: a production build never does), and say so
// through `source`. The message fixture is keyed by id; an id it lacks
// rethrows the real error, because a fixture miss is not an absence.
async function loadCommunicationsFixture() {
  const m = await import('@/fixtures/v3-communications.fixture.json');
  return m.default;
}
async function loadMessageFixture() {
  const m = await import('@/fixtures/v3-message.fixture.json');
  return m.default;
}
// NOT CONNECTED YET (2026-10-05). Until v3-mail and v3-docs merge, their
// routes do not exist on the server, and Express answers them with server.js's
// unbuilt-route 404, { error: 'Not found', path } (no `ok`; every v3 router
// answer carries ok: false). That exact answer means "this section is not
// connected": the call passes it to api.js as expected (no toast, no
// TRACON_DEBUG record) and the section says so in a quiet line. Any other
// failure, a 404 from a mounted v3 route included, is not matched and toasts
// as before. Guard: tests/v3-palette-and-sections-browser.test.js.
export const NOT_CONNECTED = 'not_connected';
export function isRouteMissing(body) {
  return !!body && typeof body === 'object' && body.error === 'Not found' && typeof body.path === 'string' && !('ok' in body);
}
const ROUTE_MISSING = [{ status: 404, code: 'Not found', body: isRouteMissing }];
const routeMissing = (e) => e instanceof ApiError && e.status === 404 && isRouteMissing(e.body);

export async function getV3Communications(accountId, { limit, before } = {}) {
  const qs = new URLSearchParams();
  if (limit) qs.set('limit', String(limit));
  if (before) qs.set('before', before);
  const q = qs.toString();
  try {
    const data = await api.get(`/api/v3/account/${encodeURIComponent(accountId)}/communications${q ? `?${q}` : ''}`, { expected: ROUTE_MISSING });
    return { data, source: 'api' };
  } catch (e) {
    if (routeMissing(e)) return { data: null, source: NOT_CONNECTED };
    if (!fallsBack(e)) throw e;
    return { data: await loadCommunicationsFixture(), source: 'fixture' };
  }
}

// Is v3-docs on this server? GET /api/v3/account/:id/documents is the read
// v3-docs adds beside its upload and confirm doors; the unbuilt-route answer
// is "not connected". A DEV build with the API off keeps the drop zone (the
// fixture flow). Any other failure throws (api.js has toasted it).
export async function getV3DocumentsConnected(accountId) {
  try {
    await api.get(`/api/v3/account/${encodeURIComponent(accountId)}/documents`, { expected: ROUTE_MISSING });
    return { connected: true };
  } catch (e) {
    if (routeMissing(e)) return { connected: false };
    if (fallsBack(e)) return { connected: true };
    throw e;
  }
}
export async function getV3Message(id) {
  try {
    const data = await api.get(`/api/v3/messages/${encodeURIComponent(id)}`);
    return { data, source: 'api' };
  } catch (e) {
    if (!fallsBack(e)) throw e;
    const all = await loadMessageFixture();
    if (!all[id]) throw e;
    return { data: { ok: true, message: all[id] }, source: 'fixture' };
  }
}

// THE DROP ZONE'S DOORS moved (paperwork-1 L1b-B1): the account drop zone files through the deal's document door
// (services/deals.service.js fileDealDocuments, POST /api/tracon/deals/:dealId/documents). The v3 upload and confirm doors
// (POST /api/v3/documents, /:id/confirm) were never routed on main and are retired with their fixture guess.

// THE LINK DOORS (account-people-door T26, T27): David's three acts on who is on
// an account. Each resolves the server's answer: { action, link | previous_account_id,
// read_back (the stores re-read), warnings }; at account_people "zoho" each
// answers 409 account_people_not_record with the broker's sentence in `message`.
// No fixture fallback: a write cannot be faked.
export function addV3PersonLink({ account_id, person_id, role }) {
  return api.post('/api/v3/people/links', { account_id, person_id, role });
}

export function endV3PersonLink(linkId, { account_id, reason }) {
  return api.post(`/api/v3/people/links/${encodeURIComponent(linkId)}/end`, { account_id, reason });
}

export function setV3MainAccount({ account_id, person_id }) {
  return api.post('/api/v3/people/main', { account_id, person_id });
}

// Resolves { data: { ok, q, results }, source }. Under 2 characters the
// contract answers an empty list and so does this, without a call.
export async function searchV3(q) {
  const query = (q || '').trim();
  if (query.length < 2) return { data: { ok: true, q: query, results: [] }, source: 'api' };
  try {
    const data = await api.get(`/api/v3/search?q=${encodeURIComponent(query)}`);
    return { data, source: 'api' };
  } catch (e) {
    if (!fallsBack(e)) throw e;
    return { data: fixtureSearch(await loadFixture(), query), source: 'fixture' };
  }
}

// The fixture's own rows, matched the way the contract says (substring on the
// account name, prefix on the registration with the N stripped, token prefix
// on a person's name, policy number stripped of spaces and dashes with a
// prefix match on 4 or more characters, phone on the last 7 or the full 10 or
// 11 digits). Enough for the palette to render every row type against.
function fixtureSearch(fixture, q) {
  const norm = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const reg = norm(q).replace(/^N/, '');
  const a = fixture.account;
  const href = `/v3/account/${a.id}`;
  const results = [];
  const pol = norm(q);
  for (const t of fixture.terms) {
    const pn = norm(t.policy_number);
    if (pn && pol && (pol.length >= 4 ? pn.startsWith(pol) : pn === pol)) {
      results.push({ type: 'policy', id: t.id, label: t.policy_number, sublabel: `${t.carrier?.name || 'carrier not stated'} · ${String(t.period?.effective_date || '').slice(0, 4)}-${String(t.period?.expiration_date || '').slice(0, 4)} · ${a.name}`, account_id: a.id, account_name: a.name, href: `${href}?term=${t.id}`, match: { field: 'policy_number', value: t.policy_number }, score: 95 });
    }
  }
  const digits = String(q).replace(/\D/g, '');
  for (const p of fixture.people) {
    const pd = String(p.phone || '').replace(/\D/g, '');
    if (pd && digits.length >= 7 && (pd.endsWith(digits) && (digits.length === 7 || digits.length >= 10))) {
      results.push({ type: 'person', id: p.id, label: p.display_name, sublabel: `${p.pilot ? 'pilot' : 'contact'} · ${a.name}`, account_id: a.id, account_name: a.name, href, match: { field: 'phone', value: p.phone }, score: 75 });
    }
  }
  for (const ac of fixture.aircraft) {
    if (reg && norm(ac.registration).replace(/^N/, '').startsWith(reg)) {
      results.push({ type: 'aircraft', id: ac.id, label: ac.registration, sublabel: `${ac.year} ${ac.make} ${ac.model} · ${a.name}`, account_id: a.id, account_name: a.name, href, match: { field: 'registration', value: ac.registration }, score: 100 });
    }
  }
  const tokens = q.toLowerCase().split(/\s+/).filter(Boolean);
  for (const p of fixture.people) {
    const names = [p.legal_name.first, p.legal_name.middle, p.legal_name.last, p.preferred_name].filter(Boolean).map((s) => s.toLowerCase());
    if (tokens.length && tokens.every((t) => names.some((n) => n.startsWith(t)))) {
      results.push({ type: 'person', id: p.id, label: p.display_name, sublabel: `${p.pilot ? 'pilot' : 'contact'} · ${a.name}`, account_id: a.id, account_name: a.name, href, match: { field: 'last_name', value: p.legal_name.last }, score: 90 });
    }
  }
  if (a.name.toLowerCase().replace(/[^a-z0-9]/g, '').includes(q.toLowerCase().replace(/[^a-z0-9]/g, ''))) {
    results.push({ type: 'account', id: a.id, label: a.name, sublabel: `${a.entity_type.value || 'entity type not stated'} · ${fixture.terms.length} terms`, account_id: a.id, account_name: a.name, href, match: { field: 'name', value: a.name }, score: 80 });
  }
  return { ok: true, q, results: results.sort((x, y) => y.score - x.score) };
}

// THE FLAG, AS THE CLIENT SEES IT (V3 Overnight 3, lane C): GET /api/v3/status
// answers 200 only when V3_API_ENABLED is on and the caller is a broker, and
// 404 v3_api_disabled when it is off. lib/v3-flag.js is its one reader. No
// fixture fallback: the fixture is not a flag, and a DEV build with the API
// off is the flag off.
export function getV3Status() {
  // 404 v3_api_disabled is the answer "off", expected on every load while the flag is off (api.js options.expected).
  return api.get('/api/v3/status', { expected: [{ status: 404, code: 'v3_api_disabled' }] });
}
